"use client";

import { useEffect, useRef, useState } from "react";
import { RotateCcw, SkipForward, Snowflake } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import styles from "./arena.module.css";
import { prefersReducedMotion, useSeenOnce } from "./Reveal";
import {
  SAMPLE_FINAL_STEP,
  SAMPLE_PROBLEMS,
  SAMPLE_STEP_MS,
  sampleStage,
  sampleStandings,
  sampleStepAt,
  type SampleCellState,
} from "./sample-standings";

const ROW_HEIGHT = 46;

const CELL_CLASS: Readonly<Record<SampleCellState, string>> = {
  ac: "bg-emerald-50 text-emerald-700",
  wa: "bg-red-50 text-red-700",
  fz: "bg-blue-50 text-blue-700",
  no: "bg-transparent text-neutral-300",
};

/**
 * The bento's sample standings (`EX-PUB19`): frozen, then unfrozen one cell
 * at a time, bottom up, the way a contest hall watches it, with a balloon for
 * each solve. It plays once when the card comes into view and rests on the
 * final board; Replay plays it again. The replay runs longer than five
 * seconds, so while it plays the same button stops it on the final board
 * (WCAG 2.2.2). Under reduced motion it shows the final board and never moves.
 * Made-up teams, and the card says so.
 */
export function SampleStandings() {
  const t = useTranslations("judge.home");
  const ref = useRef<HTMLDivElement>(null);
  const seen = useSeenOnce(ref, 0.35);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [still, setStill] = useState(false);

  useEffect(() => {
    setStill(prefersReducedMotion());
  }, []);

  useEffect(() => {
    if (!seen || still || startedAt !== null) return;
    const at = Date.now() + 400;
    setStartedAt(at);
    setNow(Date.now());
  }, [seen, still, startedAt]);

  const { step, sub } = still ? { step: SAMPLE_FINAL_STEP, sub: 0 } : sampleStepAt(startedAt === null ? null : now - startedAt);
  const playing = startedAt !== null && !still && step < SAMPLE_FINAL_STEP;
  /** Before it starts and while it plays, the button skips to the end; after, it replays. */
  const finished = startedAt !== null && step >= SAMPLE_FINAL_STEP;

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, [playing]);

  const rows = sampleStandings(step, sub);
  const stage = sampleStage(step);

  function replay() {
    const at = Date.now();
    setStartedAt(at);
    setNow(at);
  }

  function skip() {
    const at = Date.now();
    setStartedAt(at - SAMPLE_STEP_MS * SAMPLE_FINAL_STEP);
    setNow(at);
  }

  return (
    <div ref={ref} className="relative mt-6 overflow-hidden rounded-xl border border-neutral-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-neutral-100 bg-neutral-50 px-3.5 py-2.5 text-xs text-neutral-500">
        <span>{t("standingsSample")}</span>
        <span className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold transition-colors duration-300 ease-out",
              stage === "final" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700",
            )}
          >
            <Snowflake className="h-3.5 w-3.5" aria-hidden />
            {stage === "frozen" ? t("standingsFrozen") : stage === "unfreezing" ? t("standingsUnfreezing") : t("standingsFinal")}
          </span>
          {still ? null : (
            <Button
              type="button"
              variant="tertiary"
              onClick={finished ? replay : skip}
              className="h-7 gap-1.5 rounded-full border border-neutral-200 bg-white px-2.5 text-xs text-neutral-600 hover:border-primary/50 hover:bg-white hover:text-primary"
            >
              {finished ? (
                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <SkipForward className="h-3.5 w-3.5" aria-hidden />
              )}
              {finished ? t("standingsReplay") : t("standingsSkip")}
            </Button>
          )}
        </span>
      </div>

      {/* The board is one picture to assistive technology; the button above stays outside it, reachable. */}
      <div role="img" aria-label={t("standingsAria")}>
        <div
          className="grid h-[34px] grid-cols-[16px_minmax(0,1fr)_repeat(5,24px)_24px] items-center gap-0.5 border-b border-neutral-100 px-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-neutral-400 sm:grid-cols-[32px_minmax(0,1fr)_repeat(5,44px)_52px_60px] sm:gap-1.5 sm:px-3.5"
          aria-hidden
        >
          <span>#</span>
          <span>{t("standingsTeam")}</span>
          {SAMPLE_PROBLEMS.map((letter) => (
            <span key={letter} className="text-center">
              {letter}
            </span>
          ))}
          <span className="text-center">
            <span className="sm:hidden">=</span>
            <span className="hidden sm:inline">{t("standingsSolved")}</span>
          </span>
          <span className="hidden text-right sm:block">{t("standingsPenalty")}</span>
        </div>

        <div className="relative" style={{ height: rows.length * ROW_HEIGHT }} aria-hidden>
          {rows.map((row) => (
            <div
              key={row.name}
              className={cn(
                styles.standingsRow,
                "absolute inset-x-0 grid h-[46px] grid-cols-[16px_minmax(0,1fr)_repeat(5,24px)_24px] items-center gap-0.5 border-b border-neutral-100 px-2.5 sm:grid-cols-[32px_minmax(0,1fr)_repeat(5,44px)_52px_60px] sm:gap-1.5 sm:px-3.5",
                row.highlighted ? "bg-primary/5" : "bg-white",
              )}
              style={{ top: (row.rank - 1) * ROW_HEIGHT }}
            >
              <span className={cn(styles.mono, "text-[13px] font-bold text-neutral-600")}>{row.rank}</span>
              <span className="truncate text-[12.5px] font-medium text-neutral-900 sm:text-[13.5px]">{row.name}</span>
              {row.cells.map((cell, index) => (
                <span
                  key={SAMPLE_PROBLEMS[index]}
                  className={cn(
                    styles.standingsCell,
                    "relative flex h-7 flex-col items-center justify-center rounded-md text-[11px] font-bold leading-none sm:h-8 sm:text-xs",
                    CELL_CLASS[cell.state],
                    cell.picked && "ring-2 ring-primary",
                  )}
                >
                  {cell.mark}
                  {cell.detail ? (
                    <small className={cn(styles.mono, "mt-0.5 hidden text-[10px] font-medium opacity-75 sm:block")}>
                      {cell.detail}
                    </small>
                  ) : null}
                  {cell.balloon ? (
                    <svg className={styles.balloon} viewBox="0 0 20 30" aria-hidden>
                      <ellipse cx="10" cy="10" rx="8" ry="9.5" className={styles.balloonBody} />
                      <path d="M8.4 19.1h3.2L10 21.2z" className={styles.balloonBody} />
                      <path d="M10 21.2c-1.6 2.8 1.6 4.8 0 8" className={styles.balloonString} />
                    </svg>
                  ) : null}
                </span>
              ))}
              <span className={cn(styles.mono, "text-center text-sm font-bold text-neutral-900")}>{row.solved}</span>
              <span className={cn(styles.mono, "hidden text-right text-[12.5px] text-neutral-500 sm:block")}>{row.penalty}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
